import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';
import UserNav from '../../components/navbar/user-nav';
import Breadcrumb from '../../components/Breadcrumb';

interface Miembro {
  id: string; // doc.id de Firestore
  nombre: string;
  nombreEnFlex?: string;
  correo: string;
  tipo: 'asociado' | 'asesor';
  inmobiliaria_doc_id?: string;
  inmobiliaria_doc_ids?: string[]; // Para afiliados (array)
  propiedadesAsignadas: number;
}

interface Propiedad {
  id: string; // doc.id de Firestore
  list_number?: string;
  titulo?: string;
  tipo_propiedad?: string;
  precio_actual_mxn?: number;
  inmobiliaria_doc_id?: string;
  asesor_doc_ids?: string[];
  asociado_doc_id?: string;
}

interface PropiedadCSV {
  listNumber: string;
  agenteNombre: string;
  agenciaNombre: string;
  tipo: string;
  precioMXN: string;
}

interface MatchResult {
  miembro: Miembro;
  propiedadesCSV: PropiedadCSV[];
  propiedadesFirestore: Propiedad[];
  propiedadesSinAsignar: Propiedad[];
}

interface Inmobiliaria {
  id: string;
  nombre: string;
  nombreNormalizado: string;
}

const AsignacionMasivaPropiedades: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();

  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [csvPropiedades, setCsvPropiedades] = useState<PropiedadCSV[]>([]);
  const [csvMiembros, setCsvMiembros] = useState<{ nombre: string; nombreEnFlex: string; tipo: string; agencia: string; correo: string }[]>([]);
  const [miembros, setMiembros] = useState<Miembro[]>([]);
  const [propiedades, setPropiedades] = useState<Propiedad[]>([]);
  const [inmobiliarias, setInmobiliarias] = useState<Inmobiliaria[]>([]);
  const [matchResults, setMatchResults] = useState<MatchResult[]>([]);
  const [logs, setLogs] = useState<string[]>([]);
  const [stats, setStats] = useState({
    miembrosSinPropiedades: 0,
    propiedadesSinAsignar: 0,
    matchesEncontrados: 0,
    propiedadesParaAsignar: 0
  });

  const addLog = (msg: string) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()} - ${msg}`]);
    if (process.env.NODE_ENV === "development") console.log(msg);
  };

  // Normalizar string para comparación
  const normalizeString = (str: string): string => {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Cargar CSV de propiedades
  const handlePropiedadesCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());

      const data: PropiedadCSV[] = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        // Manejar campos con comas dentro de comillas
        const values: string[] = [];
        let current = '';
        let inQuotes = false;

        for (const char of line) {
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim());

        // Headers: listNumber,tipo,formato,estado,agenciaNombre,agenciaEncontrada,agenteNombre,agenteEncontrado,precioUSD,precioMXN,...
        const listNumberIdx = headers.indexOf('listNumber');
        const agenteNombreIdx = headers.indexOf('agenteNombre');
        const agenciaNombreIdx = headers.indexOf('agenciaNombre');
        const tipoIdx = headers.indexOf('tipo');
        const precioMXNIdx = headers.indexOf('precioMXN');

        if (values[agenteNombreIdx]) {
          data.push({
            listNumber: values[listNumberIdx] || '',
            agenteNombre: values[agenteNombreIdx] || '',
            agenciaNombre: values[agenciaNombreIdx] || '',
            tipo: values[tipoIdx] || '',
            precioMXN: values[precioMXNIdx] || '0'
          });
        }
      }

      setCsvPropiedades(data);
      addLog(`📄 CSV Propiedades cargado: ${data.length} registros con agente asignado`);
    };
    reader.readAsText(file);
  };

  // Cargar CSV de miembros
  const handleMiembrosCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');

      const data: { nombre: string; nombreEnFlex: string; tipo: string; agencia: string; correo: string }[] = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const values: string[] = [];
        let current = '';
        let inQuotes = false;

        for (const char of line) {
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim());

        // Headers: Nombre,Tipo,Nombre en Flex,Agencia,Correo,...
        if (values.length >= 5) {
          data.push({
            nombre: values[0] || '',
            tipo: values[1] || '',
            nombreEnFlex: values[2] || values[0] || '',
            agencia: values[3] || '',
            correo: values[5] || values[4] || '' // correo-flex o correo
          });
        }
      }

      setCsvMiembros(data);
      addLog(`📄 CSV Miembros cargado: ${data.length} registros`);
    };
    reader.readAsText(file);
  };

  // Cargar datos de Firestore
  const cargarDatosFirestore = async () => {
    addLog('🔄 Cargando datos de Firestore...');

    try {
      // Cargar inmobiliarias
      const inmoSnap = await getDocs(collection(db, 'inmobiliaria'));
      const inmosData = inmoSnap.docs.map(d => ({
        id: d.id,
        nombre: d.data().nombre || '',
        nombreNormalizado: normalizeString(d.data().nombre || '')
      }));
      setInmobiliarias(inmosData);
      addLog(`   ✅ ${inmosData.length} inmobiliarias cargadas`);

      // Cargar asociados
      const asociadosSnap = await getDocs(collection(db, 'asociado'));
      const asociadosData: Miembro[] = asociadosSnap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          nombre: data.nombre || '',
          nombreEnFlex: data.nombreEnFlex || data.nombre || '',
          correo: data.correo || '',
          tipo: 'asociado' as const,
          inmobiliaria_doc_id: data.inmobiliaria_doc_id,
          propiedadesAsignadas: 0
        };
      });

      // Cargar asesores (afiliados)
      const asesoresSnap = await getDocs(collection(db, 'asesor'));
      const asesoresData: Miembro[] = asesoresSnap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          nombre: data.nombre || '',
          nombreEnFlex: data.nombreEnFlex || data.nombre || '',
          correo: data.correo || '',
          tipo: 'asesor' as const,
          inmobiliaria_doc_ids: data.inmobiliaria_doc_ids,
          inmobiliaria_doc_id: data.inmobiliaria_doc_ids?.[0],
          propiedadesAsignadas: 0
        };
      });

      const todosMiembros = [...asociadosData, ...asesoresData];
      addLog(`   ✅ ${todosMiembros.length} miembros cargados (${asociadosData.length} asociados, ${asesoresData.length} afiliados)`);

      // Cargar propiedades
      const propSnap = await getDocs(collection(db, 'propiedades'));
      const propsData: Propiedad[] = propSnap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          list_number: data.list_number || '',
          titulo: data.titulo || '',
          tipo_propiedad: data.tipo_propiedad || '',
          precio_actual_mxn: data.precio_actual_mxn || 0,
          inmobiliaria_doc_id: data.inmobiliaria_doc_id,
          asesor_doc_ids: data.asesor_doc_ids || [],
          asociado_doc_id: data.asociado_doc_id
        };
      });
      addLog(`   ✅ ${propsData.length} propiedades cargadas`);

      // Contar propiedades por miembro
      const conteoProps: { [key: string]: number } = {};
      propsData.forEach(p => {
        if (p.asesor_doc_ids) {
          p.asesor_doc_ids.forEach(id => {
            conteoProps[id] = (conteoProps[id] || 0) + 1;
          });
        }
        if (p.asociado_doc_id) {
          conteoProps[p.asociado_doc_id] = (conteoProps[p.asociado_doc_id] || 0) + 1;
        }
      });

      // Actualizar conteo en miembros
      todosMiembros.forEach(m => {
        m.propiedadesAsignadas = conteoProps[m.id] || 0;
      });

      setMiembros(todosMiembros);
      setPropiedades(propsData);

      const sinProps = todosMiembros.filter(m => m.propiedadesAsignadas === 0).length;
      const propsSinAsignar = propsData.filter(p => !p.asesor_doc_ids?.length && !p.asociado_doc_id).length;

      setStats(prev => ({
        ...prev,
        miembrosSinPropiedades: sinProps,
        propiedadesSinAsignar: propsSinAsignar
      }));

      addLog(`📊 Resumen: ${sinProps} miembros sin propiedades, ${propsSinAsignar} propiedades sin asignar`);

      return { miembros: todosMiembros, propiedades: propsData, inmobiliarias: inmosData };
    } catch (error: any) {
      addLog(`❌ Error cargando Firestore: ${error.message}`);
      throw error;
    }
  };

  // Buscar inmobiliaria por nombre
  const findInmobiliariaDocId = (agenciaNombre: string, inmos: Inmobiliaria[]): string | null => {
    if (!agenciaNombre) return null;
    const normalizado = normalizeString(agenciaNombre);

    // Búsqueda exacta
    let found = inmos.find(i => i.nombreNormalizado === normalizado);
    if (found) return found.id;

    // Búsqueda parcial
    found = inmos.find(i =>
      i.nombreNormalizado.includes(normalizado) ||
      normalizado.includes(i.nombreNormalizado)
    );
    return found?.id || null;
  };

  // Analizar y encontrar matches
  const analizarMatches = async () => {
    if (csvPropiedades.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'CSV de propiedades requerido',
        text: 'Carga el archivo propiedades-completas.csv primero',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    setAnalyzing(true);
    setMatchResults([]);
    setLogs([]);

    try {
      addLog('🚀 Iniciando análisis de asignación masiva...');

      // Cargar datos de Firestore
      const { miembros: miembrosFS, propiedades: propsFS, inmobiliarias: inmosFS } = await cargarDatosFirestore();

      // Filtrar miembros sin propiedades o con pocas
      const miembrosSinProps = miembrosFS.filter(m => m.propiedadesAsignadas === 0);
      addLog(`🔍 Analizando ${miembrosSinProps.length} miembros sin propiedades asignadas...`);

      const results: MatchResult[] = [];
      let totalPropiedadesParaAsignar = 0;

      for (const miembro of miembrosSinProps) {
        // Buscar el nombre en Flex del miembro
        let nombreParaBuscar = miembro.nombreEnFlex || miembro.nombre;

        // Si tenemos CSV de miembros, buscar el nombre en Flex correcto
        if (csvMiembros.length > 0) {
          const miembroCSV = csvMiembros.find(m =>
            normalizeString(m.correo) === normalizeString(miembro.correo) ||
            normalizeString(m.nombre) === normalizeString(miembro.nombre)
          );
          if (miembroCSV?.nombreEnFlex) {
            nombreParaBuscar = miembroCSV.nombreEnFlex;
          }
        }

        const nombreNormalizado = normalizeString(nombreParaBuscar);

        // Buscar propiedades en CSV que correspondan a este miembro
        const propiedadesDelMiembroCSV = csvPropiedades.filter(p =>
          normalizeString(p.agenteNombre) === nombreNormalizado
        );

        if (propiedadesDelMiembroCSV.length > 0) {
          // Buscar estas propiedades en Firestore por list_number
          const listNumbers = propiedadesDelMiembroCSV.map(p => p.listNumber);

          const propiedadesEnFirestore = propsFS.filter(p =>
            listNumbers.includes(p.list_number || '')
          );

          // Filtrar las que no tienen asignación
          const propiedadesSinAsignar = propiedadesEnFirestore.filter(p =>
            (!p.asesor_doc_ids || p.asesor_doc_ids.length === 0) && !p.asociado_doc_id
          );

          if (propiedadesSinAsignar.length > 0) {
            results.push({
              miembro,
              propiedadesCSV: propiedadesDelMiembroCSV,
              propiedadesFirestore: propiedadesEnFirestore,
              propiedadesSinAsignar
            });

            totalPropiedadesParaAsignar += propiedadesSinAsignar.length;

            addLog(`   ✅ ${miembro.nombre}: ${propiedadesSinAsignar.length} propiedades para asignar (de ${propiedadesDelMiembroCSV.length} en CSV)`);
          }
        }
      }

      setMatchResults(results);
      setStats(prev => ({
        ...prev,
        matchesEncontrados: results.length,
        propiedadesParaAsignar: totalPropiedadesParaAsignar
      }));

      addLog(`📊 Análisis completado: ${results.length} miembros con propiedades para asignar, total ${totalPropiedadesParaAsignar} propiedades`);

      if (results.length === 0) {
        Swal.fire({
          icon: 'info',
          title: 'Sin matches encontrados',
          text: 'No se encontraron propiedades sin asignar que correspondan a miembros sin propiedades.',
          confirmButtonColor: '#3085d6'
        });
      }

    } catch (error: any) {
      addLog(`❌ Error: ${error.message}`);
    } finally {
      setAnalyzing(false);
    }
  };

  // Asignar todas las propiedades encontradas
  const asignarTodas = async () => {
    if (matchResults.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin propiedades para asignar',
        text: 'Primero ejecuta el análisis',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    const totalProps = matchResults.reduce((acc, r) => acc + r.propiedadesSinAsignar.length, 0);

    const confirm = await Swal.fire({
      icon: 'question',
      title: '¿Asignar todas las propiedades?',
      html: `
        <p>Se asignarán <strong>${totalProps}</strong> propiedades a <strong>${matchResults.length}</strong> miembros.</p>
        <p class="text-muted">Cada propiedad se asignará al miembro y a su inmobiliaria correspondiente.</p>
      `,
      showCancelButton: true,
      confirmButtonText: 'Sí, asignar todas',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745'
    });

    if (!confirm.isConfirmed) return;

    setLoading(true);
    let asignadas = 0;
    let errores = 0;

    try {
      for (const result of matchResults) {
        const miembro = result.miembro;
        const inmobiliariaDocId = miembro.inmobiliaria_doc_id || miembro.inmobiliaria_doc_ids?.[0];

        addLog(`📝 Asignando ${result.propiedadesSinAsignar.length} propiedades a ${miembro.nombre}...`);

        // Usar batch para eficiencia
        const batch = writeBatch(db);

        for (const propiedad of result.propiedadesSinAsignar) {
          try {
            const propRef = doc(db, 'propiedades', propiedad.id);

            const updates: any = {
              asesor_doc_ids: [miembro.id],
              fecha_asignacion: new Date().toISOString(),
              asignado_por: 'asignacion-masiva'
            };

            // Asignar asociado_doc_id si es asociado
            if (miembro.tipo === 'asociado') {
              updates.asociado_doc_id = miembro.id;
            }

            // Asignar inmobiliaria si el miembro tiene una
            if (inmobiliariaDocId) {
              updates.inmobiliaria_doc_id = inmobiliariaDocId;
            }

            batch.update(propRef, updates);
            asignadas++;
          } catch (error: any) {
            addLog(`   ❌ Error propiedad ${propiedad.list_number}: ${error.message}`);
            errores++;
          }
        }

        await batch.commit();
        addLog(`   ✅ Batch completado para ${miembro.nombre}`);
      }

      addLog(`🎉 Asignación completada: ${asignadas} exitosas, ${errores} errores`);

      Swal.fire({
        icon: 'success',
        title: '¡Asignación completada!',
        html: `
          <p><strong>${asignadas}</strong> propiedades asignadas correctamente</p>
          ${errores > 0 ? `<p class="text-danger">${errores} errores</p>` : ''}
        `,
        confirmButtonColor: '#28a745'
      });

      // Limpiar resultados
      setMatchResults([]);

    } catch (error: any) {
      addLog(`❌ Error general: ${error.message}`);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message,
        confirmButtonColor: '#d33'
      });
    } finally {
      setLoading(false);
    }
  };

  // Asignar propiedades de un miembro específico
  const asignarMiembro = async (result: MatchResult) => {
    const { miembro, propiedadesSinAsignar } = result;
    const inmobiliariaDocId = miembro.inmobiliaria_doc_id || miembro.inmobiliaria_doc_ids?.[0];

    const confirm = await Swal.fire({
      icon: 'question',
      title: `¿Asignar propiedades a ${miembro.nombre}?`,
      html: `
        <p>Se asignarán <strong>${propiedadesSinAsignar.length}</strong> propiedades</p>
        <p><strong>Inmobiliaria:</strong> ${inmobiliariaDocId ? inmobiliariaDocId.substring(0, 12) + '...' : 'Sin asignar'}</p>
      `,
      showCancelButton: true,
      confirmButtonText: 'Sí, asignar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745'
    });

    if (!confirm.isConfirmed) return;

    setLoading(true);

    try {
      const batch = writeBatch(db);

      for (const propiedad of propiedadesSinAsignar) {
        const propRef = doc(db, 'propiedades', propiedad.id);

        const updates: any = {
          asesor_doc_ids: [miembro.id],
          fecha_asignacion: new Date().toISOString(),
          asignado_por: 'asignacion-masiva'
        };

        if (miembro.tipo === 'asociado') {
          updates.asociado_doc_id = miembro.id;
        }

        if (inmobiliariaDocId) {
          updates.inmobiliaria_doc_id = inmobiliariaDocId;
        }

        batch.update(propRef, updates);
      }

      await batch.commit();

      addLog(`✅ ${propiedadesSinAsignar.length} propiedades asignadas a ${miembro.nombre}`);

      // Remover de resultados
      setMatchResults(prev => prev.filter(r => r.miembro.id !== miembro.id));

      Swal.fire({
        icon: 'success',
        title: '¡Asignación completada!',
        text: `${propiedadesSinAsignar.length} propiedades asignadas a ${miembro.nombre}`,
        confirmButtonColor: '#28a745'
      });

    } catch (error: any) {
      addLog(`❌ Error: ${error.message}`);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message,
        confirmButtonColor: '#d33'
      });
    } finally {
      setLoading(false);
    }
  };

  // Verificar permisos
  if (!user?.tipo?.includes('admin')) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          <h4>Acceso denegado</h4>
          <p>Solo administradores pueden acceder a esta página.</p>
          <button className="btn btn-primary" onClick={() => navigate('/admin-dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <UserNav />
      <div className="container py-4">
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: '/admin-dashboard' },
            { label: 'Asignación Masiva de Propiedades' }
          ]}
        />

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-primary text-white">
            <h4 className="mb-0">
              <i className="fas fa-magic me-2"></i>
              Asignación Masiva de Propiedades
            </h4>
          </div>
          <div className="card-body">
            <div className="alert alert-info">
              <h5><i className="fas fa-info-circle me-2"></i>¿Cómo funciona?</h5>
              <ol className="mb-0">
                <li>Carga el CSV de <strong>propiedades-completas.csv</strong> (contiene agenteNombre)</li>
                <li>Opcionalmente carga el CSV de <strong>miembros-ampi - miembros-ampi-procesado.csv</strong> (para mejor match de nombres)</li>
                <li>El sistema analiza qué miembros no tienen propiedades y busca sus propiedades en el CSV</li>
                <li>Hace match por <strong>Nombre en Flex</strong> del miembro con <strong>agenteNombre</strong> de la propiedad</li>
                <li>Asigna las propiedades al miembro y a su inmobiliaria</li>
              </ol>
            </div>

            {/* Carga de archivos */}
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="card border-primary h-100">
                  <div className="card-header bg-primary text-white">
                    <i className="fas fa-home me-2"></i>CSV Propiedades (Requerido)
                  </div>
                  <div className="card-body">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handlePropiedadesCSV}
                      className="form-control mb-2"
                    />
                    <small className="text-muted">propiedades-completas.csv</small>
                    {csvPropiedades.length > 0 && (
                      <div className="mt-2 text-success">
                        <i className="fas fa-check-circle me-1"></i>
                        {csvPropiedades.length} propiedades con agente
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-secondary h-100">
                  <div className="card-header bg-secondary text-white">
                    <i className="fas fa-users me-2"></i>CSV Miembros (Opcional)
                  </div>
                  <div className="card-body">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleMiembrosCSV}
                      className="form-control mb-2"
                    />
                    <small className="text-muted">miembros-ampi - miembros-ampi-procesado.csv</small>
                    {csvMiembros.length > 0 && (
                      <div className="mt-2 text-success">
                        <i className="fas fa-check-circle me-1"></i>
                        {csvMiembros.length} miembros cargados
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="d-flex gap-3 mb-4">
              <button
                className="btn btn-primary btn-lg"
                onClick={analizarMatches}
                disabled={analyzing || csvPropiedades.length === 0}
              >
                {analyzing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Analizando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-2"></i>
                    1. Analizar Matches
                  </>
                )}
              </button>

              <button
                className="btn btn-success btn-lg"
                onClick={asignarTodas}
                disabled={loading || matchResults.length === 0}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Asignando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-double me-2"></i>
                    2. Asignar Todas ({stats.propiedadesParaAsignar})
                  </>
                )}
              </button>
            </div>

            {/* Estadísticas */}
            {stats.matchesEncontrados > 0 && (
              <div className="row mb-4">
                <div className="col-md-3">
                  <div className="card bg-info text-white">
                    <div className="card-body text-center">
                      <h3>{stats.miembrosSinPropiedades}</h3>
                      <small>Miembros sin propiedades</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card bg-warning text-dark">
                    <div className="card-body text-center">
                      <h3>{stats.propiedadesSinAsignar}</h3>
                      <small>Props sin asignar en Firestore</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card bg-success text-white">
                    <div className="card-body text-center">
                      <h3>{stats.matchesEncontrados}</h3>
                      <small>Miembros con matches</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card bg-primary text-white">
                    <div className="card-body text-center">
                      <h3>{stats.propiedadesParaAsignar}</h3>
                      <small>Propiedades para asignar</small>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Resultados de matches */}
            {matchResults.length > 0 && (
              <div className="card border-success mb-4">
                <div className="card-header bg-success text-white">
                  <i className="fas fa-list me-2"></i>
                  Matches Encontrados: {matchResults.length} miembros
                </div>
                <div className="card-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <div className="accordion" id="matchesAccordion">
                    {matchResults.map((result, idx) => (
                      <div className="accordion-item" key={idx}>
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#collapse${idx}`}
                          >
                            <div className="d-flex justify-content-between w-100 me-3">
                              <span>
                                <strong>{result.miembro.nombre}</strong>
                                <span className={`badge ms-2 ${result.miembro.tipo === 'asociado' ? 'bg-success' : 'bg-info'}`}>
                                  {result.miembro.tipo}
                                </span>
                              </span>
                              <span className="badge bg-primary">
                                {result.propiedadesSinAsignar.length} propiedades
                              </span>
                            </div>
                          </button>
                        </h2>
                        <div id={`collapse${idx}`} className="accordion-collapse collapse" data-bs-parent="#matchesAccordion">
                          <div className="accordion-body">
                            <p><strong>Nombre en Flex:</strong> {result.miembro.nombreEnFlex}</p>
                            <p><strong>Correo:</strong> {result.miembro.correo}</p>
                            <p><strong>Inmobiliaria Doc ID:</strong> {result.miembro.inmobiliaria_doc_id || 'Sin asignar'}</p>

                            <h6>Propiedades para asignar:</h6>
                            <table aria-label="Tabla de Asignacion Masiva Propiedades" className="table table-sm table-striped">
                              <thead>
                                <tr>
                                  <th scope="col">List Number</th>
                                  <th scope="col">Tipo</th>
                                  <th scope="col">Precio MXN</th>
                                </tr>
                              </thead>
                              <tbody>
                                {result.propiedadesSinAsignar.map((p, pIdx) => (
                                  <tr key={pIdx}>
                                    <td><code>{p.list_number}</code></td>
                                    <td>{p.tipo_propiedad}</td>
                                    <td>${p.precio_actual_mxn?.toLocaleString()}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => asignarMiembro(result)}
                              disabled={loading}
                            >
                              <i className="fas fa-check me-1"></i>
                              Asignar estas {result.propiedadesSinAsignar.length} propiedades
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Logs */}
            {logs.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <i className="fas fa-terminal me-2"></i>
                  Registro de operaciones
                </div>
                <div className="card-body bg-dark text-light" style={{ maxHeight: '300px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {logs.map((log, idx) => (
                    <div key={idx} className={
                      log.includes('❌') ? 'text-danger' :
                      log.includes('✅') ? 'text-success' :
                      log.includes('⚠️') ? 'text-warning' :
                      log.includes('🎉') ? 'text-success fw-bold' :
                      'text-info'
                    }>
                      {log}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AsignacionMasivaPropiedades;
